import { useAuth0 } from '@auth0/auth0-react';
import { ThemeProvider, Grid } from '@mui/material';
import React, { useEffect, useState } from 'react';

import { Loader, LoaderColorProps } from './components/atoms/Loader';
import { ToastViewport } from './components/organisms/ToastViewport';
import { lightTheme } from './configs/themes';
import { Error } from './pages/Error';
import { useAuthUser } from './queries/user-query';
import AppRoutes from './routes';
import { initializeQZTrayCertificate } from './utils/print-label-utils';
import { getUser, setToken, setUser } from './utils/users';

// The imported `Error` page component shadows the global Error in this module,
// so extract a log-safe message without `instanceof`. Logs only `.message`
// because the underlying request error can carry the user's email (PII).
const getErrorMessage = (error: unknown): string =>
  (error as { message?: string })?.message ?? 'unknown error';

const App = () => {
  const {
    user,
    isAuthenticated,
    isLoading: auth0Loading,
    error,
    getAccessTokenSilently,
  } = useAuth0();
  // Start as true so there's no flash between Auth0 finishing and our token fetch starting
  const [isLoading, setIsLoading] = useState(true);
  const [provisioningFailed, setProvisioningFailed] = useState(false);
  const hasError = user?.email_verified === false || user?.blocked === true || !!error;
  const authQuery = useAuthUser(user);

  useEffect(() => {
    if (auth0Loading) return; // Still processing Auth0 callback
    if (isAuthenticated) {
      bootstrapAuthenticatedUser();
    } else {
      setIsLoading(false); // Auth0 done, not authenticated — stop loading
    }
    // `bootstrapAuthenticatedUser` is deliberately omitted: it closes over
    // `authQuery`, which useMutation gives a new identity every render, so
    // listing it here would re-run the bootstrap (and re-POST the user) on
    // every render. The auth state above is the only thing that should retrigger it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, auth0Loading]);

  // Fetch the access token, then provision/confirm the CMS2 user record BEFORE
  // we stop loading. The routed app (and the permission + visibility + userLogin
  // queries it fires on mount) must not render until the backend user row
  // exists — otherwise a brand-new SSO user races those authenticated reads
  // against their own creation and the BFF 500s on the missing row.
  const bootstrapAuthenticatedUser = async () => {
    setIsLoading(true);
    setProvisioningFailed(false);
    try {
      const token = await getAccessTokenSilently();
      if (!token) return;
      setToken(token);
      setUser(user);

      const userObj = getUser();
      if (userObj?.sub) {
        try {
          // Resolves once the user is confirmed to exist (or has been created)
          // in CMS2. authQuery is intentionally not driven via an effect dep —
          // useMutation returns a new object each render, which would loop.
          await authQuery.mutateAsync(userObj);
        } catch (provisionError) {
          // Provisioning failed — surface a clear error screen rather than
          // dropping the user into a half-initialised app with no permissions
          // or visibility.
          console.error('Failed to provision CMS2 user on login:', getErrorMessage(provisionError));
          setProvisioningFailed(true);
        }
      }
    } catch (tokenError) {
      // Token retrieval failed (e.g. an expired session). Don't show the error
      // screen — let AppRoutes mount so withAuthenticationRequired redirects
      // back to Auth0 for re-authentication.
      console.error('Failed to obtain access token on login:', getErrorMessage(tokenError));
    } finally {
      // Always stop loading so a failure never strands the user on the spinner.
      setIsLoading(false);
    }
  };

  useEffect(() => {
    initializeQZTrayCertificate();
  }, []);

  // Show loader while Auth0 is processing the code= callback, fetching the
  // token, or provisioning the CMS2 user. This prevents the home page from
  // flashing during the OAuth redirect and holds the route tree's authenticated
  // queries back until the user row is ready.
  if (isLoading || auth0Loading) {
    return (
      <Grid mt="200px" justifyContent="center" display="flex">
        <Loader color={LoaderColorProps.Success} />
      </Grid>
    );
  }

  if (hasError || provisioningFailed) {
    return <Error provisioningFailed={provisioningFailed} />;
  }

  return (
    <ThemeProvider theme={lightTheme}>
      <AppRoutes />
      {/* Mounted once, beside the routes rather than inside a layout: this is
          the only point that covers both the desktop and mobile route trees and
          survives navigation. */}
      <ToastViewport />
    </ThemeProvider>
  );
};

export default App;
